import type { ProethicaTestKind } from "@constants/demo/proethica-tests";
import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { useAppTheme } from "@hooks/useAppTheme";
import { Box, DonutChart } from "@memorang/ui";
import { type ComponentProps, Fragment } from "react";
import { Card, Divider, Text } from "react-native-paper";
import { getProethicaCompletionDistribution } from "../../helpers/proethica-home-stats";
import type { ProethicaProgressData } from "../../helpers/proethica-progress-data";
import { ModuleThumbnail } from "../ModuleThumbnail";
import { IconAvatar } from "./IconAvatar";

type Props = ProethicaProgressData["completion"];

type StatusRow = {
	icon?: ComponentProps<typeof Icon>["name"];
	testKind?: ProethicaTestKind;
	title: string;
	subtitle: string;
	badge: string;
	done: boolean;
};

const Pill = ({
	label,
	done,
	leadingIcon,
}: {
	label: string;
	done: boolean;
	leadingIcon?: ComponentProps<typeof Icon>["name"];
}) => {
	const theme = useAppTheme();
	const color = done ? theme.colors.primary : theme.colors.onSurfaceVariant;
	return (
		<Box
			flexDirection="row"
			alignItems="center"
			justifyContent="center"
			paddingHorizontal={10}
			paddingVertical={5}
			style={{
				gap: 4,
				borderRadius: theme.roundness,
				backgroundColor: done
					? theme.colors.primaryContainer
					: theme.colors.surfaceVariant,
			}}
		>
			{leadingIcon ? <Icon name={leadingIcon} size={13} color={color} /> : null}
			<Text
				variant="labelMedium"
				style={{ color, fontWeight: "600", lineHeight: 16 }}
			>
				{label}
			</Text>
		</Box>
	);
};

const CompletionStatusRow = ({
	icon,
	testKind,
	title,
	subtitle,
	badge,
	done,
}: StatusRow) => {
	const theme = useAppTheme();
	return (
		<Box
			flexDirection="row"
			alignItems="center"
			padding={16}
			style={{ gap: 12 }}
		>
			{testKind ? (
				<ModuleThumbnail size={40} title={title} isTest testKind={testKind} />
			) : (
				<IconAvatar
					icon={icon ?? "book-open-outline"}
					background={
						done ? theme.colors.primaryContainer : theme.colors.surfaceVariant
					}
					color={done ? theme.colors.primary : theme.colors.onSurfaceVariant}
					iconSize={18}
				/>
			)}
			<Box flex={1} style={{ gap: 2 }}>
				<Text
					variant="titleSmall"
					style={{ color: theme.colors.onSurface, fontWeight: "600" }}
				>
					{title}
				</Text>
				<Text
					variant="bodySmall"
					style={{ color: theme.colors.onSurfaceVariant }}
				>
					{subtitle}
				</Text>
			</Box>
			<Pill label={badge} done={done} />
		</Box>
	);
};

export const ProethicaCompletionCard = ({
	doneCount,
	totalCount,
	remaining,
	allDone,
	preTestCompleted,
	postTestPassed,
}: Props) => {
	const theme = useAppTheme();

	const percent =
		totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;
	const distribution = getProethicaCompletionDistribution({
		completed: doneCount,
		total: totalCount,
		percent,
	});

	const statusRows: StatusRow[] = [
		{
			icon: "format-list-bulleted",
			title: "Modules",
			subtitle: `${doneCount} of ${totalCount} done, ${remaining} remaining`,
			badge: allDone ? "Done" : "In Progress",
			done: allDone,
		},
		{
			testKind: "pretest",
			title: "Pre-Test",
			subtitle: preTestCompleted
				? "Completed, baseline recorded"
				: "Not started yet",
			badge: preTestCompleted ? "Done" : "Pending",
			done: preTestCompleted,
		},
		{
			testKind: "posttest",
			title: "Post-Test",
			subtitle: postTestPassed
				? "Completed, requirement passed"
				: "Not passed yet",
			badge: postTestPassed ? "Passed" : "Pending",
			done: postTestPassed,
		},
	];

	return (
		<Card
			mode="outlined"
			style={{
				backgroundColor: theme.colors.surface,
				borderColor: theme.colors.outlineVariant,
				borderRadius: theme.roundness,
				overflow: "hidden",
			}}
		>
			<Box
				flexDirection="row"
				alignItems="center"
				padding={16}
				style={{ gap: 12 }}
			>
				<IconAvatar
					icon="check"
					background={theme.colors.primaryContainer}
					color={theme.colors.primary}
					iconSize={22}
				/>
				<Box flex={1} style={{ gap: 2 }}>
					<Text
						variant="titleMedium"
						style={{ color: theme.colors.onSurface, fontWeight: "700" }}
					>
						Completion
					</Text>
					<Text
						variant="bodySmall"
						style={{ color: theme.colors.onSurfaceVariant }}
					>
						{doneCount} of {totalCount} modules done
					</Text>
				</Box>
			</Box>

			<Divider />

			<Box alignItems="center" paddingVertical={24}>
				<DonutChart distribution={distribution} radius={72} innerRadius={58}>
					<Box alignItems="center">
						<Text
							variant="headlineSmall"
							style={{ color: theme.colors.onSurface, fontWeight: "700" }}
						>
							{doneCount} / {totalCount}
						</Text>
						<Text
							variant="bodySmall"
							style={{ color: theme.colors.onSurfaceVariant }}
						>
							modules done
						</Text>
					</Box>
				</DonutChart>

				<Box
					flexDirection="row"
					flexWrap="wrap"
					justifyContent="center"
					paddingHorizontal={16}
					style={{ gap: 8, marginTop: 20 }}
				>
					<Pill
						label="Pre-test done"
						done={preTestCompleted}
						leadingIcon={preTestCompleted ? "check" : "circle-outline"}
					/>
					<Pill
						label={`${doneCount}/${totalCount} modules`}
						done={allDone}
						leadingIcon={allDone ? "check" : "circle-outline"}
					/>
					<Pill
						label="Post-test passed"
						done={postTestPassed}
						leadingIcon={postTestPassed ? "check" : "circle-outline"}
					/>
				</Box>
			</Box>

			<Divider />

			{statusRows.map((row, index) => (
				<Fragment key={row.title}>
					{index > 0 ? <Divider /> : null}
					<CompletionStatusRow {...row} />
				</Fragment>
			))}
		</Card>
	);
};
